import axios_real from 'axios';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaCapsules } from 'react-icons/fa';
import { FiCopy } from 'react-icons/fi';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from '../../api/axios';
import FullscreenLoading from '../../components/FullScreenLoader';
import HeaderWithBack from '../../components/HeaderWithBack';
import DeliveryModal from '../../components/modal/Delivery';
import PharmacyModal from '../../components/modal/Pharmacy';
import { HospitalAddOn } from '../../types/HospitalAddon';
import PaymentMethods from './Components/PaymentMethod';
import { assetUrl } from '../../utils/assets';

const EprescriptionOrder: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { prescriptionId } = useParams<{ prescriptionId: string }>();

  const storedData = sessionStorage.getItem('userData');
  const userData = storedData ? JSON.parse(storedData) : null;
  const token = userData ? userData.token : null;

  const [loading, setLoading] = useState(false);
  const [prescriptionData, setPrescriptionData] = useState<any>(null);
  const [paymentMethod, setPaymentMethod] = useState<any[]>([]);

  // States untuk form
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [recipientAddress, setRecipientAddress] = useState('');
  const [recipientNote, setRecipientNote] = useState('');
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);

  const [outletId, setOutletId] = useState<string | null>(null);
  const [outlet, setOutlet] = useState('');
  const [shippingMethod, setShippingMethod] = useState('');
  const [shippingCost, setShippingCost] = useState(0);
  const [feeService, setFeeService] = useState(0);
  const [cost, setCost] = useState(0);

  const [selectedPaymentMethodID, setSelectedPaymentMethodID] = useState<string | null>(null);
  const [selectedPaymentSubID, setSelectedPaymentSubID] = useState<string | null>(null);

  const [showAddressModal, setShowAddressModal] = useState(false);
  const [isPharmacyModalOpen, setPharmacyModalOpen] = useState(false);
  const [isDeliveryModalOpen, setDeliveryModalOpen] = useState(false);
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showRedeemInfoModal, setShowRedeemInfoModal] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const hasFetched = useRef(false);

  // Fetch prescription data
  const getPrescriptionData = () => {
    if (!token || !prescriptionId) return;
    
    axios
      .get(`prescription/get-prescription-datas/${prescriptionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        setPrescriptionData(response.data);
        
        // Populate form dengan data dari response
        if (response.data?.order) {
          setRecipientName(response.data.order.recipient_name || '');
          setRecipientPhone(response.data.order.recipient_phone || '');
          setRecipientAddress(response.data.order.recipient_address || '');
          setRecipientNote(response.data.order.recipient_note || '');
          setLat(response.data.order.lat ? parseFloat(response.data.order.lat) : null);
          setLng(response.data.order.lng ? parseFloat(response.data.order.lng) : null);
        }

        setLoading(false);
      })
      .catch((error) => {
        console.error('Gagal mengambil resep', error);
        setLoading(false);
      });
  };

  const getPaymentMethods = () => {
    if (!token) return;
    
    axios
      .get('payment?configuration=category_payment_live_consultation', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        setPaymentMethod(response.data.data || []);
      })
      .catch((error) => {
        console.error('Error fetching payment methods:', error);
      });
  };

  // Set default shipping option
  const defaultShippingOption = useMemo(() => {
    const shippingOptions = prescriptionData?.pharmacy?.shipping ?? [];
    if (!shippingOptions || shippingOptions.length === 0) return null;

    const instant = shippingOptions.find(
      (opt: any) => opt.shipping_method?.toUpperCase() === 'INSTANT'
    );
    if (instant) return instant;

    const reguler = shippingOptions.find(
      (opt: any) => opt.shipping_method?.toUpperCase() === 'REGULER'
    );
    if (reguler) return reguler;

    return shippingOptions[0];
  }, [prescriptionData?.pharmacy?.shipping]);

  // Initialize with default shipping
  useEffect(() => {
    if (defaultShippingOption) {
      setOutletId(prescriptionData?.pharmacy?.outlets?.outlet_id || null);
      setOutlet(prescriptionData?.pharmacy?.outlets?.outlet || '');
      setShippingCost(defaultShippingOption.shipping_cost || 0);
      setFeeService(Number(defaultShippingOption.fee_service) || 0);
      setShippingMethod(defaultShippingOption.shipping_method || '');
    }
  }, [defaultShippingOption, prescriptionData]);

  useEffect(() => {
    setCost(feeService + shippingCost);
  }, [shippingCost, feeService]);

  useEffect(() => {
    if (!prescriptionId) {
      navigate('/not-found');
      return;
    }

    if (prescriptionId && !hasFetched.current) {
      setLoading(true);
      getPrescriptionData();
      getPaymentMethods();
      hasFetched.current = true;
    }
  }, [prescriptionId, token]);

  // Check if prescription is already redeemed
  useEffect(() => {
    if (prescriptionData?.prescription?.is_download) {
      setShowRedeemInfoModal(true);
    }
  }, [prescriptionData]);

  // Show payment modal if transaction exists
  useEffect(() => {
    const status = prescriptionData?.transaction?.status_payment;
    if (status === 'draft' || status === 'payment') {
      setShowPaymentModal(true);
    }
  }, [prescriptionData]);

  const handlePharmacySelect = (outlet: { outlet_id: string; outlet: string }) => {
    setOutletId(outlet.outlet_id);
    setOutlet(outlet.outlet);
    setDeliveryModalOpen(true);
  };

  const handleOpenAddressPage = () => {
    navigate(`/e-prescription/address-page/${prescriptionId}`,{
          state: {
              addressState:prescriptionData?.order?.recipient_address,
              nameState: prescriptionData?.patient?.name ? prescriptionData?.patient?.name : prescriptionData?.order?.recipient_name,
              phoneState: prescriptionData?.order?.recipient_phone ? prescriptionData?.order?.recipient_phone : prescriptionData?.patient?.phone,
              noteState: prescriptionData?.order?.recipient_note,
              latState: prescriptionData?.order?.lat,
              lngState: prescriptionData?.order?.lng
          }
      });
  };

  const handleAddressSave = (alamatBaru: string, labelBaru: string, newLat: number, newLng: number) => {
    setRecipientAddress(alamatBaru);
    setLat(newLat);
    setLng(newLng);
    setShowAddressModal(false);
  };

  const handleDeliverySave = (option: string, fee: number, service: number) => {
    setShippingMethod(option);
    setShippingCost(fee);
    setFeeService(service);
    setDeliveryModalOpen(false);
  };

  const formReady = prescriptionId && outletId && shippingMethod && selectedPaymentSubID && cost > 0;

  const [shippingDiscount, setShippingDiscount] = useState(0);
  const [baseShippingDiscount, setBaseShippingDiscount] = useState(0);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<any>(null);
  const [selectedPaymentpaymentId, setSelectedPaymentpaymentId] = useState<number | null>(null);
  useEffect(() => {
    if (prescriptionData?.practitioner?.hospital_id) {
        const hospitalId = prescriptionData.practitioner.hospital_id;
        axios
        .get(`/hospital-add-ons?organization_id=${hospitalId}`)
        .then((res) => {
            if (res.data?.data) {
                const addons: HospitalAddOn[] = res.data.data;
                const discount = addons.find((item) => item.key === 'price_shipping_discount');
                const discountValue = parseInt(discount?.value?.toString() || '0');
                setBaseShippingDiscount(discountValue);
                setShippingDiscount(discountValue);
            }
        })
        .catch(() => {
            setBaseShippingDiscount(0);
            setShippingDiscount(0);
        });
    }
    }, [prescriptionData]);
  const handlePaymentSelect = (paymentId: number, paymentMethodData: any) => {
        setSelectedPaymentpaymentId(paymentId);
        setSelectedPaymentMethod(paymentMethodData);
        
        const virtualAccountCodes = ['VA', 'I1', 'BT', 'B1', 'M2', 'BR', 'BC', 'AG', 'S1', 'A1', 'NC'];
        const eWalletCodes = ['OV', 'LA', 'SA', 'DA'];

        const code = paymentMethodData?.code?.toUpperCase() || '';

        const isVirtualAccount = virtualAccountCodes.includes(code);
        const isEwallet = eWalletCodes.includes(code);

        const currentCost = feeService + shippingCost;
        const minTotal = 10000;
        let newDiscount = baseShippingDiscount;

        if (isVirtualAccount || isEwallet) {
            const currentTotal = currentCost - newDiscount;
            if (currentTotal < minTotal) {
                const adjustment = minTotal - currentTotal;
                newDiscount = Math.max(0, baseShippingDiscount - adjustment);
            }
        }

        setShippingDiscount(newDiscount);
    };

  const handleConfirmation = async () => {
    if (loading) return;

    try {
      setLoading(true);
      const response = await axios.post(
        'prescription/create-prescription-order',
        {
          prescription_id: prescriptionId,
          prescription_code: prescriptionData?.prescription?.prescription_code,
          practitioner_id: prescriptionData?.prescription?.practitioner_id,
          outlet_id: outletId,
          outlet: outlet,
          shipping_method: shippingMethod,
          shipping_cost: shippingCost,
          fee_service: feeService,
          total_price: cost,
          payment_method_id: selectedPaymentSubID,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const res = response.data;

      if (res.status === 'success' && res.data?.payment?.statusMessage === 'SUCCESS') {
        const paymentLink = res.data.payment.paymentUrl;
        if (paymentLink) {
          window.location.href = paymentLink;
        }
      }

      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      if (axios_real.isAxiosError(error)) {
        const msg = (error.response?.data as any)?.message || 'Terjadi kesalahan';
        if (msg === 'is_redeemed') {
          window.location.reload();
        }
      }
      setLoading(false);
    }
  };

  const handleLanjutPembayaran = async () => {
    try {
      setRedirecting(true);
      const response = await axios.get(`prescription/get-prescription-datas/${prescriptionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const newData = response.data;
      setPrescriptionData(newData);
      const isRedeem = newData?.prescription?.is_download;
      const trx = newData?.transaction;

      if (!isRedeem && trx?.url_payment) {
        window.location.href = trx.url_payment;
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setRedirecting(false);
    }
  };

  const handleCloseDialog = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-white pb-20">
      {loading && <FullscreenLoading message="Mohon tunggu..." />}
      <HeaderWithBack title="E-Prescription" />

      <div className="p-4 space-y-3 bg-gray-100">
        {/* Alamat Card */}
        <div className="relative bg-white p-4 rounded-xl shadow-sm border">
          <p className="text-sm text-gray-500 mb-1">Alamat</p>
          <p className="font-semibold text-sm">{recipientAddress}</p>
          <p className="text-sm text-gray-700">{recipientNote}</p>
          <p className="text-sm text-gray-500">{recipientName} • {recipientPhone}</p>
          <button
            className="absolute top-4 right-4 text-gray-500"
            onClick={handleOpenAddressPage}
          >
            <img src={assetUrl("images/pencil-simple.svg")} alt="Edit Alamat" className="w-4 h-4" />
          </button>
        </div>

        {/* Dokter Card */}
        {prescriptionData?.practitioner && (
          <div className="bg-white p-4 rounded-xl shadow-sm border">
            <p className="font-semibold text-sm">{prescriptionData.practitioner.dr_name}</p>
            <p className="text-sm text-gray-500">{prescriptionData.practitioner.hospital}</p>
            <p className="text-sm text-gray-700 border-t pt-1 mt-1">
              {prescriptionData.patient.name} ({prescriptionData.patient.birth_date} y.o)
            </p>
          </div>
        )}

        {/* Apotek & Obat Card */}
        <div className="bg-white p-4 rounded-xl shadow-sm border space-y-3">
          <div className="flex items-start gap-2">
            <img src={assetUrl("images/storefront.svg")} alt="Pin Lokasi" className="w-6 h-6 mt-1" />
            <div>
              <p className="font-semibold text-sm">{outlet || 'Pilih Apotek'}</p>
              <p className="text-sm text-gray-500">Apotek terdekat</p>
            </div>
          </div>

          {/* List Obat */}
          {prescriptionData?.items && prescriptionData.items.length > 0 ? (
            prescriptionData.items.map((item: any, index: number) => (
              <div key={item.prescription_item_id} className="flex items-start justify-between mb-3">
                <div className="flex gap-2">
                  <FaCapsules className="w-6 h-6 text-teal-500" />
                  <div>
                    <p className="text-sm font-medium">{item.drug_name.trim()} x {item.quantity} {item.unit}</p>
                    <p className="text-sm text-gray-500">({item.frequency_dd}x{item.frequency})</p>
                    {item.time && <p className="text-xs text-gray-400 italic">{item.time}</p>}
                  </div>
                </div>
                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">Ditanggung</span>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500">Memuat obat...</p>
          )}
        </div>

        {/* Pengiriman Card */}
        <div
          className="bg-white p-4 rounded-xl shadow-sm border flex items-center justify-between cursor-pointer"
          onClick={() => setShowShippingModal(true)}
        >
          <div className="flex items-start gap-2">
          <img src={assetUrl("images/frame.svg")} alt="Delivery" className="w-6 h-6 mt-1" />
            <div>
              <p className="text-sm font-medium">
                {shippingMethod ? `${shippingMethod} • Rp${new Intl.NumberFormat('id-ID').format(cost)}` : 'Belum ada metode pengiriman'}
              </p>
              <p className="text-sm text-gray-500">
                {shippingMethod
                  ? `Estimasi Tiba: ${shippingMethod === 'INSTANT' ? '±2 Jam' : 'Maks 3 Hari'}`
                  : 'Silakan pilih metode pengiriman'}
              </p>
            </div>
          </div>
          <img src={assetUrl("images/arrow-right.svg")} alt="Chevron" className="w-4 h-4" />
        </div>

        {/* Modal Shipping */}
        {showShippingModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
            <div className="bg-white w-full rounded-t-2xl p-4">
              <div className="flex justify-center mb-4">
                <div className="w-10 h-1 bg-gray-400 rounded-full" />
              </div>
              <p className="font-semibold text-base mb-4">Metode Pengiriman</p>

              {Array.isArray(prescriptionData?.pharmacy?.shipping) && prescriptionData.pharmacy.shipping.length > 0 ? (
                prescriptionData.pharmacy.shipping.map((option: any) => {
                  const totalBiaya = option.fee_service + option.shipping_cost;
                  let estimasi = '-';
                  if (option.shipping_method?.toUpperCase() === 'REGULER') {
                    estimasi = 'Estimasi Tiba: Maks 3 Hari';
                  } else if (option.shipping_method?.toUpperCase() === 'INSTANT') {
                    estimasi = 'Estimasi Tiba: ±2 Jam';
                  }

                  return (
                    <div
                      key={option.shipping_method}
                      onClick={() => {
                        setShippingCost(option.shipping_cost);
                        setFeeService(option.fee_service);
                        setShippingMethod(option.shipping_method);
                        setShowShippingModal(false);
                      }}
                      className="py-3 border-t last:border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                    >
                      <p className="text-sm font-medium">
                        {option.shipping_method} • Rp{new Intl.NumberFormat('id-ID').format(totalBiaya)}
                      </p>
                      <p className="text-sm text-gray-500">{estimasi}</p>
                    </div>
                  );
                })
              ) : (
                <p className="text-center text-gray-500 text-sm">Tidak ada metode pengiriman tersedia.</p>
              )}

              <button
                onClick={() => setShowShippingModal(false)}
                className="mt-4 w-full text-center text-sm text-blue-600"
              >
                Tutup
              </button>
            </div>
          </div>
        )}

        {/* Metode Pembayaran Card */}
        <PaymentMethods paymentMethods={paymentMethod} onSelect={handlePaymentSelect}/>

        {/* Detail Transaksi Card */}
        <div className="bg-white p-4 rounded-xl shadow-sm border space-y-2">
          <div className="flex justify-between text-sm">
            <span>Biaya Obat</span>
            <span className="text-blue-600 text-sm bg-blue-100 px-2 py-0.5 rounded-full">Ditanggung</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Biaya Pengiriman</span>
            <span>Rp{new Intl.NumberFormat('id-ID').format(cost)}</span>
          </div>
          <div className="flex justify-between text-sm">
              <span>Potongan Ongkos Kirim</span>
              <span>-Rp{new Intl.NumberFormat('id-ID').format(shippingDiscount)}</span>
          </div>
          {cost > 0 && (
            <>
              <div className="border-t border-dashed border-gray-300 my-4" />
              <div className="flex justify-between text-sm font-semibold text-red-600">
                <span>Total</span>
                <span>Rp{new Intl.NumberFormat('id-ID').format(cost)}</span>
              </div>
            </>
          )}
        </div>

        {/* Payment Modal */}
        {showPaymentModal && prescriptionData?.transaction && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
            <div className="bg-white w-full rounded-t-2xl p-4">
              <div className="flex justify-center mb-4">
                <div className="w-10 h-1 bg-gray-400 rounded-full" />
              </div>
              <p className="font-semibold text-base mb-4 text-center">Akun Pembayaran</p>

              {(() => {
                const trx = prescriptionData.transaction;
                const expiredUtc = new Date(trx.expired_date + 'Z');
                const isExpired = new Date() > expiredUtc;
                const isPaid = trx.status_payment === 'payment';

                return (
                  <>
                    <div className="flex items-center mb-3">
                      <img src={trx.image} alt="Bank Logo" className="w-8 h-4 mr-3 rounded" />
                      <div className="text-sm font-medium">{trx.bank}</div>
                    </div>

                    {trx?.bank?.toUpperCase() !== 'QRIS' && trx?.va && (
                      <div className="flex items-center justify-between border px-4 py-2 rounded-lg bg-gray-100 mb-4">
                        <span className="font-mono text-lg text-gray-800">{trx.va}</span>
                        {!isExpired && !isPaid && (
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(trx.va);
                              alert('Nomor VA berhasil disalin!');
                            }}
                            className="text-blue-600 hover:text-blue-800"
                            title="Salin nomor VA"
                          >
                            <FiCopy size={20} />
                          </button>
                        )}
                      </div>
                    )}

                    {isPaid ? (
                      <div className="text-sm text-center text-green-600 font-semibold mb-4">
                        ✅ Pembayaran telah berhasil diterima.
                      </div>
                    ) : isExpired ? (
                      <div className="text-sm text-center text-red-600 font-semibold mb-4">
                        ❌ Waktu pembayaran telah berakhir. Silakan buat ulang pembayaran.
                      </div>
                    ) : (
                      <div className="text-sm text-center text-gray-600 font-medium mb-4">
                        Berlaku hingga: {expiredUtc.toLocaleString()}
                      </div>
                    )}

                    {isPaid ? (
                      <button
                        className="block w-full bg-green-400 text-white py-2 text-center rounded-full font-semibold"
                        onClick={() => handleCloseDialog()}
                      >
                        Pembayaran Selesai
                      </button>
                    ) : !isExpired && trx.url_payment ? (
                      <button
                        onClick={handleLanjutPembayaran}
                        disabled={redirecting}
                        className="block w-full bg-red-500 text-white py-2 text-center rounded-full font-semibold"
                      >
                        {redirecting ? 'Mengalihkan...' : 'Lanjut ke Pembayaran'}
                      </button>
                    ) : (
                      <button
                        onClick={() => setShowPaymentModal(false)}
                        className="block w-full bg-red-400 text-white py-2 text-center rounded-full font-semibold"
                      >
                        Buat Pembayaran Ulang
                      </button>
                    )}
                  </>
                );
              })()}

              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  handleCloseDialog();
                }}
                className="mt-4 w-full text-center text-sm text-blue-600"
              >
                Tutup
              </button>
            </div>
          </div>
        )}

        {/* Redeem Info Modal */}
        {showRedeemInfoModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-end">
            <div className="bg-white w-full rounded-t-2xl p-6">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-1 bg-gray-300 rounded-full" />
              </div>
              <p className="font-semibold text-base mb-2 text-center text-gray-800">Resep Telah Ditebus</p>
              <p className="text-sm text-center text-gray-600 mb-6">
                Resep ini sudah diunduh dan tidak dapat ditebus ulang. Jika Anda membutuhkan resep baru,
                silakan hubungi dokter Anda.
              </p>
              <button
                onClick={() => {
                  setShowRedeemInfoModal(false);
                  handleCloseDialog();
                }}
                className="block w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded-full font-semibold transition-colors"
              >
                Mengerti
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Button */}
      {prescriptionData?.transaction?.status_payment === 'payment' || prescriptionData?.prescription?.is_download ? (
        ''
      ) : (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4">
          <button
            className={`w-full py-3 rounded-full font-semibold transition ${
              formReady && !loading ? 'bg-teal-500 text-white hover:bg-teal-600' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            onClick={handleConfirmation}
            disabled={!formReady || loading}
          >
            {loading ? 'Memproses...' : 'Konfirmasi'}
          </button>
        </div>
      )}

      <PharmacyModal
        isOpen={isPharmacyModalOpen}
        onClose={() => setPharmacyModalOpen(false)}
        onPharmacySelect={handlePharmacySelect}
        onSave={(alamatBaru: string) => setRecipientAddress(alamatBaru)}
        alamatBaru={recipientAddress}
        prescription_id={Number(prescriptionId)}
      />

      <DeliveryModal
        isOpen={isDeliveryModalOpen}
        onClose={() => setDeliveryModalOpen(false)}
        pharmacyName={outlet}
        pharmacyId={outletId || ''}
        prescriptionId={Number(prescriptionId)}
        onSave={handleDeliverySave}
      />
    </div>
  );
};

export default EprescriptionOrder;
